import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Pencil } from 'lucide-react';
import PortalShell from '@/components/PortalShell';
import AutoCursor from '@/components/AutoCursor';
import InfoSpot from '@/components/InfoSpot';

function WizardStepper({ active }) {
  const steps = ['Basic Details', 'Assign Roles', 'Review'];
  return (
    <div className="flex items-center justify-center py-4 px-6 bg-[#FAFAFA] rounded-xl mb-5">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex flex-col items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 ${
              i < active ? 'bg-teal-600 border-teal-600 text-white' :
              i === active ? 'bg-white border-teal-600 text-teal-600' :
              'bg-white border-[#E5E7EB] text-[#737373]'
            }`}>{i < active ? '✓' : <span className="w-2 h-2 rounded-full bg-current" />}</div>
            <span className={`text-[12px] font-body ${i <= active ? 'font-medium text-[#0A0A0A]' : 'text-[#737373]'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-24 h-[2px] mx-2 mt-[-18px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
        </div>
      ))}
    </div>
  );
}

function ReviewRow({ label, value, delay }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay }}>
      <p className="text-[11px] font-body font-medium text-[#737373]">{label}</p>
      <p className="text-[13px] font-body text-[#0A0A0A] mt-0.5">{value}</p>
    </motion.div>
  );
}

export default function UserFormReviewScene({ onNext }) {
  const cursorPath = [
    { x: 400, y: 160, delay: 1.0 },   // scan basic details
    { x: 700, y: 260, delay: 1.0 },
    { x: 400, y: 500, delay: 1.2 },   // scan roles
    { x: 560, y: 620, delay: 1.4, click: true }, // click Add
  ];

  return (
    <PortalShell
      activeItem="User"
      overlay={<AutoCursor targets={cursorPath} startDelay={0.8} />}
    >
      <div data-testid="scene-user-form-review" className="px-8 pt-5 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-[12px] font-body text-[#737373] mb-3">
            Manage <ChevronRight size={12} className="inline" /> Users <ChevronRight size={12} className="inline" /> <span className="text-[#0A0A0A] font-medium">Add new user</span>
          </div>
          <div className="mb-4">
            <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0A0A0A] flex items-center gap-2">
              Review & Add
              <InfoSpot
                text="Last step — verify everything before sending the invite. Click the pencil icon to edit a section."
                position="right"
                color="teal"
                autoOpenAt={0.8}
                autoCloseAfter={2.5}
                glowAt={0.5}
              />
            </h2>
            <p className="mt-1 font-body text-sm text-[#737373]">Confirm details before sending the invite</p>
          </div>

          <WizardStepper active={2} />

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Basic details</h3>
                <p className="font-body text-xs text-[#737373]">Fill in the required basic information</p>
              </div>
              <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#F5F5F5]"><Pencil size={14} className="text-[#737373]" /></button>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3.5">
              <ReviewRow label="First name" value="Ahmed" delay={0.4} />
              <ReviewRow label="Last name" value="Shareef" delay={0.45} />
              <ReviewRow label="Username" value="Asamoe@yourhealth.com" delay={0.5} />
              <ReviewRow label="Business email" value="Asamoe@yourhealth.com" delay={0.55} />
              <ReviewRow label="Contact number" value="+971 512 100999" delay={0.6} />
              <ReviewRow label="User type" value="Doctor" delay={0.65} />
              <ReviewRow label="Nationality" value="Algeria" delay={0.7} />
              <ReviewRow label="Emirates ID" value="784-1939-1111111-1" delay={0.75} />
              <ReviewRow label="Organisation" value="Burjeel" delay={0.8} />
              <ReviewRow label="Linked facility" value="Burjeel - Mussafah | Burjeel Al Nahyan" delay={0.85} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
            className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Assigned roles</h3>
                <p className="font-body text-xs text-[#737373]">Roles per facility</p>
              </div>
              <button className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#F5F5F5]"><Pencil size={14} className="text-[#737373]" /></button>
            </div>
            <div className="space-y-2.5">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
                <p className="font-body font-medium text-sm text-[#0A0A0A]">Provider Facility Admin</p>
                <p className="font-body text-xs text-[#737373]">Burjeel Al Nahyan</p>
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
                <p className="font-body font-medium text-sm text-[#0A0A0A]">Provider Facility User</p>
                <p className="font-body text-xs text-[#737373]">Burjeel - Mussafah</p>
              </motion.div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="flex justify-center mt-5">
            <motion.button
              data-testid="wizard-add-user"
              onClick={onNext}
              animate={{
                scale: [1, 1, 1.1, 0.97, 1.06, 1],
                boxShadow: [
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 10px rgba(13,148,136,0.3)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 8px rgba(13,148,136,0.25)',
                  '0 0 0 0 rgba(13,148,136,0)',
                ],
              }}
              transition={{ duration: 2.0, delay: 4.6, times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm"
            >
              Add <ArrowRight size={16} />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </PortalShell>
  );
}
